import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as Edit } from 'src/Images/icons/common/edit.svg';
import { ReactComponent as CheckIcon } from 'src/Images/icons/common/CheckIcon.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as DocumentIcon } from 'src/Images/icons/planning/Document.svg';
import { ReactComponent as PackQuantityIcon } from 'src/Images/icons/planning/PackQuantity.svg';
import { ReactComponent as DeliveryQuantityIcon } from 'src/Images/icons/planning/DeliveryQuantity.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLoadLineItemsForVehicleTracking,
  updateVehicleTrackingLoadLineItem,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { useParams } from 'react-router-dom';
import getColorCode from 'src/utils/GetThemeColor';
import TableRightDrawer from 'src/components/TableRightDrawer';
import 'src/style/createVehicleTracking.css';

const LOAD_LINE_ITEM_TABLE_PREFERENCE_KEY = 'savedLoadingStationColumns';

const LOAD_LINE_ITEM_HEADERS = [
  { label: 'Delivery Number', value: '1', key: 'sap_code' },
  { label: 'Material Code', value: '2', key: 'product_sap_code' },
  { label: 'Description', value: '3', key: 'product_description' },
  { label: 'Pack Type', value: '4', key: 'pack_type' },
  { label: 'Pack Quanity', value: '5', key: 'pack_quantity' },
  { label: 'Pack Weight', value: '6', key: 'pack_weight' },
  { label: 'Batch', value: '7', key: 'batch' },
  { label: 'Plant', value: '8', key: 'plant' },
  { label: 'Actual Quantity', value: '9', key: 'actual_quantity' },
  { label: 'Receiving Quantity', value: '10', key: 'receiving_quantity' },
  { label: 'Gross Weight', value: '11', key: 'weight' },
  { label: 'Invoice Number', value: '12', key: 'invoice_number' },
  { label: 'E-way Bill Number', value: '13', key: 'eway_bill_number' },
  { label: 'IRN Number', value: '14', key: 'irn' },
  { label: 'Bill to Party', value: '15', key: 'bill_to_party' },
  { label: 'Ship to Party', value: '16', key: 'ship_to_party' },
];

const loadTablePreferences = () => {
  const saved = localStorage.getItem(LOAD_LINE_ITEM_TABLE_PREFERENCE_KEY);

  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return {
        order: parsed.order || [],
        selected: parsed.selected || [],
      };
    } catch (e) {
      return {
        order: [],
        selected: [],
      };
    }
  }

  return {
    order: [],
    selected: [],
  };
};

const getLoadLineItemColumnValue = (fieldKey, item) => {
  switch (fieldKey) {
    case 'product_name':
      return item.product?.product_name;
    case 'product_sap_code':
      return item.product?.product_sap_code;
    case 'product_description':
      return item.product?.product_description;
    case 'plant':
      return item.plant?.name;
    case 'bill_to_party':
      return item.bill_to_party?.name;
    case 'ship_to_party':
      return item.ship_to_party?.name;
    default:
      return item[fieldKey];
  }
};

const renderLoadLineItemColumnValue = (fieldKey, item) => {
  const value = getLoadLineItemColumnValue(fieldKey, item);
  return value !== null && value !== undefined && value !== '' ? value : '-';
};

function DeliverySummaryCard({ Icon, label, value, uom, themeColor }) {
  const displayValue =
    typeof value === 'number' && uom !== 'Total' ? value.toFixed(2) : value;

  return (
    <div className="planning-dashboard-card div-flex-row div-space-between">
      <div className="div-flex-column rg-5 div-align-start">
        <span className="text-md text-semi-bold text-nowrap">{label}</span>
        <div className="div-flex-row cg-5 div-align-baseline">
          <span className="text-xxl text-bold">{displayValue}</span>
          <span className="text-md text-grey">{uom}</span>
        </div>
      </div>
      <div className="div-flex-column div-align-end">
        <span
          className={`delivery-summary-icon theme-text-color-${themeColor}`}
        >
          <Icon className="icon-size-xxxl" />
        </span>
      </div>
    </div>
  );
}

export default function LoadingStationTab() {
  const [rowIdsEnabledForEdit, setRowIdsEnabledForEdit] = useState({});
  const [isEditingAllRowEnabled, setIsEditingAllRowEnabled] = useState(false);
  const [formData, setFormData] = useState({});
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activePackTypeSummaryKey, setActivePackTypeSummaryKey] =
    useState(null);

  const { order: initialOrder, selected: initialSelected } =
    loadTablePreferences();
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(initialOrder);
  const [selectedFieldFilter, setSelectedFieldFilter] =
    useState(initialSelected);

  const { vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();

  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;

  const { loadLineItemsForVehicleTracking } = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );

  const { vehicleTrackingDetails } = vehicleTrackingDataReducer;

  const vehicleTrackingLoadLineItemData =
    loadLineItemsForVehicleTracking?.vehicleTrackingLoadLineItemData;
  const deliverySelectionSummary = useMemo(() => {
    const lineItems = vehicleTrackingLoadLineItemData || [];
    const packTypeSummaryMap = {};

    lineItems.forEach((item) => {
      const packTypeKey = item.pack_type ?? 'unassigned';
      const packTypeName = item.pack_type ?? 'Unassigned';

      if (!packTypeSummaryMap[packTypeKey]) {
        packTypeSummaryMap[packTypeKey] = {
          summaryKey: `pack-type-${packTypeKey}`,
          packTypeName,
          totalDocuments: 0,
          totalPackQuantity: 0,
          totalDeliveryQuantity: 0,
          totalGrossWeight: 0,
        };
      }

      packTypeSummaryMap[packTypeKey].totalDocuments += 1;
      packTypeSummaryMap[packTypeKey].totalPackQuantity += Number(
        item.pack_quantity || 0,
      );
      packTypeSummaryMap[packTypeKey].totalDeliveryQuantity += Number(
        item.receiving_quantity || 0,
      );
      packTypeSummaryMap[packTypeKey].totalGrossWeight += Number(
        item.weight || 0,
      );
    });

    return Object.values(packTypeSummaryMap);
  }, [vehicleTrackingLoadLineItemData]);

  const activePackTypeSummary =
    deliverySelectionSummary.find(
      (summary) => summary.summaryKey === activePackTypeSummaryKey,
    ) || deliverySelectionSummary[0];

  const summaryCards = useMemo(
    () => [
      {
        label: 'Total Documents',
        key: 'total-documents',
        value: activePackTypeSummary?.totalDocuments || 0,
        uom: 'Nos',
        Icon: DocumentIcon,
      },
      {
        label: 'Total Pack Quantity',
        key: 'total-pack-quantity',
        value: activePackTypeSummary?.totalPackQuantity || 0,
        uom: 'Nos',
        Icon: PackQuantityIcon,
      },
      {
        label: 'Total Delivery Quantity',
        key: 'total-delivery-quantity',
        value: activePackTypeSummary?.totalDeliveryQuantity || 0,
        uom: '',
        Icon: DeliveryQuantityIcon,
      },
      {
        label: 'Gross Weight',
        key: 'gross-weight',
        value: activePackTypeSummary?.totalGrossWeight || 0,
        uom: '',
        Icon: DeliveryQuantityIcon,
      },
    ],
    [activePackTypeSummary],
  );

  const filteredFieldFilter = useMemo(() => {
    const source =
      draggedFieldOrder?.length > 0 ? draggedFieldOrder : selectedFieldFilter;

    return source
      .map((value) =>
        LOAD_LINE_ITEM_HEADERS.find((field) => field.value === value),
      )
      .filter(Boolean);
  }, [draggedFieldOrder, selectedFieldFilter]);

  const canEditIssueStamp =
    vehicleTrackingDetails?.requestedStage?.entry_log_exist === true &&
    vehicleTrackingDetails?.requestedStage?.completed === false;

  const handleInputChange = (e, id) => {
    const { name } = e.target;
    let { value } = e.target;

    if (name === 'eway_bill_number') {
      value = value.replace(/\D/g, '').slice(0, 12);
    }

    setFormData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: value },
    }));
  };

  const enableRowForEditing = (item) => {
    const id = item.vehicle_tracking_load_line_item_id;
    setFormData((prev) => ({
      ...prev,
      [id]: {
        invoice_number: item.invoice_number ?? '',
        eway_bill_number: item.eway_bill_number ?? '',
        irn: item.irn ?? '',
      },
    }));
    setRowIdsEnabledForEdit((prev) => ({ ...prev, [id]: true }));
  };

  const enableAllRowForEditing = () => {
    setFormData((prev) => {
      const updated = { ...prev };
      vehicleTrackingLoadLineItemData?.forEach((item) => {
        const id = item.vehicle_tracking_load_line_item_id;
        // If row already exists in formData (user was already editing it), skip
        if (updated[id]) return;
        // Else add it fresh from reducer
        updated[id] = {
          invoice_number: item.invoice_number ?? '',
          eway_bill_number: item.eway_bill_number ?? '',
        };
      });
      return updated;
    });
    setIsEditingAllRowEnabled(true);
    setRowIdsEnabledForEdit({});
  };

  const handleSubmit = () => {
    const editedFormData = [];

    Object.keys(formData).forEach((id) => {
      const editedRow = formData[id];
      // Find the original row in the reducer
      const originalRow = vehicleTrackingLoadLineItemData?.find(
        (item) =>
          String(item.vehicle_tracking_load_line_item_id) === String(id),
      );
      if (!originalRow) return;
      // Compare — only push if something actually changed
      const invoiceChanged =
        editedRow.invoice_number !== (originalRow.invoice_number ?? '');
      const ewayChanged =
        editedRow.eway_bill_number !== (originalRow.eway_bill_number ?? '');

      if (invoiceChanged || ewayChanged) {
        editedFormData.push({
          vehicle_tracking_load_line_item_id: Number(id),
          actual_quantity: originalRow.actual_quantity,
          invoice_number: editedRow.invoice_number,
          eway_bill_number: editedRow.eway_bill_number,
        });
      }
    });
    // Nothing changed — just reset edit state
    if (!editedFormData.length) {
      resetEditState();
      return;
    }
    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      items: editedFormData,
    };
    dispatch(updateVehicleTrackingLoadLineItem(payload)).then(() => {
      resetEditState();
      dispatch(
        getLoadLineItemsForVehicleTracking(
          tenantOrganisationId,
          vehicleTrackingNumber,
        ),
      );
    });
  };

  const resetEditState = () => {
    setFormData({});
    setRowIdsEnabledForEdit({});
    setIsEditingAllRowEnabled(false);
  };

  const isRowEditing = (id) =>
    isEditingAllRowEnabled || !!rowIdsEnabledForEdit[id];

  const toggleDrawer = (open) => () => {
    setOpenDrawer(open);
  };

  const handleSelectedFieldFilter = (e, value) => {
    let updated = [...selectedFieldFilter];

    if (e.target.checked) {
      updated.push(value);
    } else {
      updated = updated.filter((fieldValue) => fieldValue !== value);
    }

    setSelectedFieldFilter(updated);
  };

  const renderEditableColumn = (fieldKey, item, rowEditing) => {
    const id = item.vehicle_tracking_load_line_item_id;

    if (fieldKey === 'invoice_number' && rowEditing) {
      return (
        <input
          name="invoice_number"
          type="text"
          className="form-input-section"
          value={formData[id]?.invoice_number || ''}
          onChange={(e) => handleInputChange(e, id)}
        />
      );
    }

    if (fieldKey === 'eway_bill_number' && rowEditing) {
      return (
        <input
          name="eway_bill_number"
          type="text"
          className="form-input-section"
          maxLength={12}
          inputMode="numeric"
          value={formData[id]?.eway_bill_number || ''}
          onChange={(e) => handleInputChange(e, id)}
        />
      );
    }

    return renderLoadLineItemColumnValue(fieldKey, item);
  };

  useEffect(() => {
    dispatch(
      getLoadLineItemsForVehicleTracking(
        tenantOrganisationId,
        vehicleTrackingNumber,
      ),
    );
  }, [dispatch, tenantOrganisationId, vehicleTrackingNumber]);

  useEffect(() => {
    if (LOAD_LINE_ITEM_HEADERS.length) {
      const fieldValues = LOAD_LINE_ITEM_HEADERS.map((field) => field.value);
      const validSelectedFields = selectedFieldFilter.filter((fieldValue) =>
        fieldValues.includes(fieldValue),
      );
      const missingFields = fieldValues.filter(
        (fieldValue) => !validSelectedFields.includes(fieldValue),
      );

      if (
        selectedFieldFilter.length === 0 ||
        validSelectedFields.length !== selectedFieldFilter.length
      ) {
        setSelectedFieldFilter([...validSelectedFields, ...missingFields]);
      }
    }
  }, [selectedFieldFilter]);

  useEffect(() => {
    localStorage.setItem(
      LOAD_LINE_ITEM_TABLE_PREFERENCE_KEY,
      JSON.stringify({
        order: draggedFieldOrder,
        selected: selectedFieldFilter,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  return (
    <div className="div-flex-column padding-top-10">
      <div className="div-flex-column rg-10 vertical-padding-10">
        {deliverySelectionSummary.length > 0 && (
          <>
            <div className="div-flex-row cg-5">
              {deliverySelectionSummary.map((packTypeSummary) => {
                const isActive =
                  packTypeSummary.summaryKey ===
                  activePackTypeSummary?.summaryKey;

                return (
                  <button
                    key={packTypeSummary.summaryKey}
                    type="button"
                    className={`div-flex-row div-align-center text-sm ${
                      isActive
                        ? `theme-pill-active-${themeColor}`
                        : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
                    }`}
                    onClick={() =>
                      setActivePackTypeSummaryKey(packTypeSummary.summaryKey)
                    }
                  >
                    <span>{packTypeSummary.packTypeName}</span>
                  </button>
                );
              })}
            </div>
            <div className="div-flex-row cg-5 delivery-selection-summary-row">
              {summaryCards.map((summaryCard) => (
                <div
                  key={summaryCard.key}
                  className="delivery-selection-summary-item"
                >
                  <DeliverySummaryCard
                    Icon={summaryCard.Icon}
                    label={summaryCard.label}
                    value={summaryCard.value}
                    uom={summaryCard.uom}
                    themeColor={themeColor}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="purchase-order-line-item-card border-radius-4">
        <div className="div-space-between div-align-center px-2 py-2 border-bottom">
          <span className="text-md text-bold delivery-header">
            Confirmed Delivery
          </span>
          <div className="div-flex-row cg-10 div-align-center">
            {canEditIssueStamp && (
              <button
                className={`edit-btn ${isEditingAllRowEnabled ? `theme-background-${themeColor}` : ''}`}
                aria-label="Edit all rows"
                onClick={
                  isEditingAllRowEnabled ? handleSubmit : enableAllRowForEditing
                }
              >
                {isEditingAllRowEnabled ? (
                  <CheckIcon
                    className="icon-size-sm"
                    style={{ color: '#FFFFFF' }}
                  />
                ) : (
                  <Edit
                    className="icon-size-md"
                    color={getColorCode(themeColor)}
                  />
                )}
              </button>
            )}
            <button
              className="icon-btn-none delivery-organiser-btn"
              onClick={toggleDrawer(true)}
              aria-label="Organiser"
              type="button"
            >
              <BulletPoints className="icon-size-lg" color="#9F9F9F" />
            </button>
          </div>
          <Drawer
            anchor="right"
            open={Boolean(openDrawer)}
            onClose={toggleDrawer(false)}
            ModalProps={{
              keepMounted: false,
            }}
            sx={{ zIndex: 1 }}
          >
            <div className="right-drawer-component-small">
              <TableRightDrawer
                toggleDrawer={toggleDrawer}
                fieldFilter={LOAD_LINE_ITEM_HEADERS}
                selectedFieldFilter={selectedFieldFilter}
                handleSelectedFieldFilter={handleSelectedFieldFilter}
                setDraggedFieldOrder={setDraggedFieldOrder}
                draggedFieldOrder={draggedFieldOrder}
              />
            </div>
          </Drawer>
        </div>
        <div className="po-table-wrapper">
          <table>
            <thead className="outward-delivery-table-head">
              <tr>
                {filteredFieldFilter.map((field) => (
                  <th key={field.value}>{field.label}</th>
                ))}
                {canEditIssueStamp && <th aria-label="Action" />}
              </tr>
            </thead>
            <tbody>
              {vehicleTrackingLoadLineItemData?.map((item) => {
                const id = item.vehicle_tracking_load_line_item_id;
                const rowEditing = isRowEditing(id);

                return (
                  <tr key={id}>
                    {filteredFieldFilter.map((field) => (
                      <td key={field.value}>
                        {renderEditableColumn(field.key, item, rowEditing)}
                      </td>
                    ))}
                    {canEditIssueStamp && (
                      <td>
                        <button
                          className={`edit-btn ${
                            rowIdsEnabledForEdit[id]
                              ? `theme-background-${themeColor}`
                              : ''
                          } ${isEditingAllRowEnabled ? 'row-edit-disabled' : ''}`}
                          aria-label="Edit"
                          disabled={isEditingAllRowEnabled}
                          onClick={() =>
                            rowIdsEnabledForEdit[id]
                              ? handleSubmit()
                              : enableRowForEditing(item)
                          }
                        >
                          {rowIdsEnabledForEdit[id] ? (
                            <CheckIcon
                              className="icon-size-sm"
                              style={{ color: '#FFFFFF' }}
                            />
                          ) : (
                            <Edit
                              className="icon-size-md"
                              color={
                                isEditingAllRowEnabled
                                  ? '#737373'
                                  : getColorCode(themeColor)
                              }
                            />
                          )}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
